import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Input from '../input';
import fuzzySearch from '../../utils/fuzzySearch';
import { useQueryParam } from '../../utils/hooks/useQueryParam';
import { useSearchParams } from 'react-router-dom';

const Search = ({
  placeholder,
  setQuery = false,
  onChange,
  miniValue,
  loading,
  options,
  fuzzyKeys,
  onSelect,
  debouncing,
  throttling,
  endIcon,
  startIcon,
  searchQuery = 'searchQuery',
  uiSearch = false,
  onClear,
  type = 'text',
  onSearch,
  disablePortal = true,
  portalClassName = '',
  maxDropdownHeight = 240, // px, only used when portal is enabled
  ...rest
}) => {
  const { paramValue, setQueryParam, removeQueryParam } =
    useQueryParam(searchQuery);
  const [searchParams, setSearchParams] = useSearchParams();
  const [modifiedOptions, setModifiedOptions] = useState(options);
  const [displayDropdown, setDisplayDropdown] = useState(false);
  const [displayMiniView, setDisplayMiniView] = useState(miniValue);
  const [localValue, setLocalValue] = useState('');
  const [portalStyles, setPortalStyles] = useState({});
  const timeoutIdRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);

  // --- Position calculation for the portal dropdown ---
  const updatePortalPosition = useCallback(() => {
    if (disablePortal || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldOpenAbove =
      spaceBelow < maxDropdownHeight && spaceAbove > maxDropdownHeight;

    const top = shouldOpenAbove ? rect.top - 4 : rect.bottom + 4;
    const maxHeight = shouldOpenAbove
      ? Math.min(maxDropdownHeight, spaceAbove - 16)
      : Math.min(maxDropdownHeight, spaceBelow - 16);

    setPortalStyles({
      position: 'fixed',
      top: shouldOpenAbove ? undefined : `${top}px`,
      bottom: shouldOpenAbove ? `${windowHeight - top}px` : undefined,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${Math.max(0, maxHeight)}px`,
      zIndex: 9999,
    });
  }, [disablePortal, maxDropdownHeight]);

  useEffect(() => {
    if (disablePortal || !displayDropdown) return;

    updatePortalPosition();

    window.addEventListener('scroll', updatePortalPosition, true);
    window.addEventListener('resize', updatePortalPosition);

    return () => {
      window.removeEventListener('scroll', updatePortalPosition, true);
      window.removeEventListener('resize', updatePortalPosition);
    };
  }, [disablePortal, displayDropdown, updatePortalPosition]);

  const handleSearch = (e) => {
    const value = e.target.value;

    if (type === 'select') {
      setLocalValue(value);
      if (value.trim() === '' && onClear) {
        onClear();
      }
    }

    if (
      searchQuery &&
      searchQuery !== null &&
      setQueryParam &&
      removeQueryParam &&
      type !== 'select'
    ) {
      if (value.trim() === '') {
        removeQueryParam();
        if (onClear) {
          onClear();
        }
      } else {
        setQueryParam(value);
      }
    }

    if (debouncing) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(() => {
        onChange(e);
        handleFuzzySearch(value);
      }, 500);
    } else if (throttling) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(() => {
        onChange(e);
        handleFuzzySearch(value);
      }, 1000);
    } else {
      onChange(e);
      handleFuzzySearch(value);
    }
  };

  useEffect(() => {
    setModifiedOptions(options);
  }, [options]);

  useEffect(() => {
    if (type === 'select') {
      setLocalValue(rest.value || '');
    }
  }, [rest.value, type]);

  const handleFuzzySearch = (val) => {
    if (uiSearch) {
      if (type === 'select' && onSearch) {
        onSearch(val);
      } else {
        const filtered = fuzzySearch(options, val, fuzzyKeys);
        setModifiedOptions(filtered);
      }
    }
  };

  const handleSelect = (opt) => {
    setDisplayDropdown(false);

    if (setQuery) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(searchQuery);
      newSearchParams.set(setQuery, opt?.id);
      setSearchParams(newSearchParams);
    } else if (searchQuery && searchQuery !== null && removeQueryParam) {
      removeQueryParam();
    }

    if (type === 'select') {
      if (searchQuery && setQueryParam && opt) {
        setQueryParam(opt.uid || opt.id);
      }
      setLocalValue('');
    }

    onSelect(opt);
  };

  // --- Click-outside checks both the input container and the (possibly portaled) dropdown ---
  const handleClickOutside = (event) => {
    const clickedInsideContainer =
      containerRef.current && containerRef.current.contains(event.target);
    const clickedInsideDropdown =
      (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
      (portalRef.current && portalRef.current.contains(event.target));

    if (!clickedInsideContainer && !clickedInsideDropdown) {
      setDisplayDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const dropdownContent = (
    <div
      ref={!disablePortal ? portalRef : dropdownRef}
      style={
        !disablePortal
          ? portalStyles
          : {}
      }
      className={
        !disablePortal
          ? 'bg-white border border-gray-400 rounded-lg shadow-lg overflow-auto z-500'
          : 'absolute z-10 w-full mt-1 bg-white border border-gray-400 rounded-lg shadow-lg max-h-[30vh] overflow-auto'
      }
    >
      {type === 'select' && (
        <div className="px-4 py-2 border-b border-gray-200">
          <input
            type="text"
            placeholder="Type to search..."
            value={localValue}
            onChange={(e) => {
              if (
                e.target.value &&
                e.target.value.trim() &&
                searchQuery &&
                removeQueryParam
              ) {
                removeQueryParam();
              }
              handleSearch(e);
            }}
            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {modifiedOptions.map((option) => (
        <div
          key={option.uid}
          className="px-4 py-2 cursor-pointer hover:bg-gray-200"
          onClick={() => handleSelect(option)}
        >
          {option.username || option.name}
        </div>
      ))}
    </div>
  );

  const dropdown =
    uiSearch && displayDropdown
      ? disablePortal
        ? dropdownContent
        : createPortal(
          <div className={portalClassName || ''}>{dropdownContent}</div>,
          document.body
        )
      : null;

  return (
    <>
      {displayMiniView ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          onClick={() => setDisplayMiniView(false)}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.8144 2.40015C6.1674 2.40015 2.40027 6.16726 2.40027 10.8142C2.40027 15.4611 6.1674 19.2283 10.8144 19.2283C12.8085 19.2283 14.6407 18.5345 16.0826 17.3752L20.0394 21.332C20.3965 21.689 20.9754 21.689 21.3324 21.332C21.6895 20.9749 21.6895 20.3961 21.3324 20.039L17.3756 16.0822C18.5348 14.6403 19.2284 12.8083 19.2284 10.8142C19.2284 6.16726 15.4613 2.40015 10.8144 2.40015ZM4.22882 10.8142C4.22882 7.17715 7.17728 4.2287 10.8144 4.2287C14.4514 4.2287 17.3999 7.17715 17.3999 10.8142C17.3999 14.4513 14.4514 17.3997 10.8144 17.3997C7.17728 17.3997 4.22882 14.4513 4.22882 10.8142Z"
            fill="#292929"
          />
        </svg>
      ) : (
        <div className="relative" ref={containerRef}>
          <Input
            type="text"
            inputClassName="!px-5"
              onClick={() => {
                setDisplayDropdown(true);
                if (!disablePortal) updatePortalPosition();
              }}
            placeholder={placeholder}
            value={
              type === 'select'
                ? localValue || rest.value || ''
                : paramValue || rest.value || ''
            }
            onChange={handleSearch}
            autoComplete="off"
            disabled={loading}
            {...rest}
          />
          {startIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center px-6">
              {startIcon}
            </div>
          )}
          {endIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center px-4">
              {endIcon}
            </div>
          )}
          {loading && (
            <div
              className={`absolute inset-y-0 ${endIcon ? 'right-5' : 'right-0'} flex items-center px-4`}
            >
              <svg
                className="w-5 h-5 text-gray-500 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647zM12 20a8 8 0 01-8-8H0c0 6.627 5.373 12 12 12v-4zm3-5.291A7.962 7.962 0 0120 12h-4c0 3.042-1.135 5.824-3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
            {disablePortal && uiSearch && displayDropdown && dropdown}
        </div>
      )}
      {!disablePortal && dropdown}
    </>
  );
};

Search.propTypes = {
  placeholder: PropTypes.string,
  searchQuery: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  fuzzyKeys: PropTypes.array,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  onSelect: PropTypes.func,
  onClear: PropTypes.func,
  onSearch: PropTypes.func,
  debouncing: PropTypes.bool,
  throttling: PropTypes.bool,
  uiSearch: PropTypes.bool,
  miniValue: PropTypes.bool,
  setQuery: PropTypes.bool,
  endIcon: PropTypes.element,
  startIcon: PropTypes.element,
  type: PropTypes.oneOf(['text', 'select']),
  disablePortal: PropTypes.bool,
  portalClassName: PropTypes.string,
  maxDropdownHeight: PropTypes.number,
};

Search.defaultProps = {
  placeholder: 'Search...',
  loading: false,
  options: [],
  debouncing: false,
  throttling: false,
  miniValue: false,
  disablePortal: true,
  portalClassName: '',
  maxDropdownHeight: 240,
};

export default Search;